import { X, Coins, Ticket, Trophy, Info } from "lucide-react";
import { useTranslation } from "react-i18next";

/** Explains the 3 modalities + the 10% platform fee. */
export default function HowItWorksModal({ open, onClose }) {
  const { t } = useTranslation();
  if (!open) return null;
  const feeItems = t("how_it_works.fee_items", { returnObjects: true }) || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="how-it-works-modal">
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-sm border border-primary/40 bg-gradient-to-b from-[#050a18] to-[#020408]">
        <div className="sticky top-0 flex items-center justify-between p-4 border-b border-primary/20 bg-black/80 backdrop-blur">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">{t("how_it_works.label")}</p>
            <h2 className="font-display font-black text-2xl tracking-tighter text-white">{t("how_it_works.title")}</h2>
          </div>
          <button onClick={onClose} data-testid="how-it-works-close"
            className="p-2 border border-primary/30 rounded-sm hover:bg-primary/10 text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-5 text-sm">

          <section className="rounded-sm border border-emerald-400/40 bg-emerald-500/5 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-emerald-300" />
              <h3 className="font-display font-black text-xl text-emerald-200">{t("how_it_works.paid_title")}</h3>
              <span className="ml-auto font-mono text-[9px] tracking-widest px-2 py-0.5 border border-emerald-400 text-emerald-200 rounded-sm">$USDT</span>
            </div>
            <p className="text-white/80">{t("how_it_works.paid_desc")}</p>
            <div className="rounded-sm bg-black/40 p-3 font-mono text-xs space-y-1">
              <p>{t("how_it_works.paid_example_gross")}<strong className="text-emerald-200">{t("how_it_works.paid_example_gross_val")}</strong></p>
              <p>{t("how_it_works.paid_example_fee")}<strong className="text-amber-200">{t("how_it_works.paid_example_fee_val")}</strong>{t("how_it_works.paid_example_fee_num")}</p>
              <p>{t("how_it_works.paid_example_net")}<strong className="text-emerald-200">{t("how_it_works.paid_example_net_val")}</strong>{t("how_it_works.paid_example_net_suffix")}</p>
              <p className="pt-1 text-muted-foreground">{t("how_it_works.paid_example_dist")}</p>
            </div>
            <p className="text-white/80">{t("how_it_works.paid_bonus")}<strong className="text-amber-200">{t("how_it_works.paid_bonus_val")}</strong>{t("how_it_works.paid_bonus_suffix")}</p>
          </section>

          <section className="rounded-sm border border-amber-300/40 bg-amber-500/5 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-amber-200" />
              <h3 className="font-display font-black text-xl text-amber-200">{t("how_it_works.ticket_title")}</h3>
              <span className="ml-auto font-mono text-[9px] tracking-widest px-2 py-0.5 border border-amber-300 text-amber-200 rounded-sm">🎟</span>
            </div>
            <p className="text-white/80">{t("how_it_works.ticket_desc_p1")}<strong>{t("how_it_works.ticket_desc_p1_val")}</strong>{t("how_it_works.ticket_desc_p1_suffix")}</p>
            <p className="text-white/80">{t("how_it_works.ticket_desc_p2")}</p>
          </section>

          <section className="rounded-sm border border-cyan-400/40 bg-cyan-500/5 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-cyan-300" />
              <h3 className="font-display font-black text-xl text-cyan-200">{t("how_it_works.tournament_title")}</h3>
              <span className="ml-auto font-mono text-[9px] tracking-widest px-2 py-0.5 border border-cyan-400 text-cyan-200 rounded-sm">FREE</span>
            </div>
            <p className="text-white/80">{t("how_it_works.tournament_desc")}<strong className="text-amber-200">{t("how_it_works.tournament_desc_val")}</strong>.</p>
            <div className="rounded-sm bg-black/40 p-3 font-mono text-xs space-y-1">
              <p>{t("how_it_works.tournament_dist_1")}<strong className="text-amber-200">{t("how_it_works.tournament_dist_1_val")}</strong>{t("how_it_works.tournament_dist_1_suffix")}</p>
              <p>{t("how_it_works.tournament_dist_2")}<strong className="text-amber-200">{t("how_it_works.tournament_dist_2_val")}</strong>{t("how_it_works.tournament_dist_2_suffix")}</p>
              <p>{t("how_it_works.tournament_dist_3")}<strong className="text-amber-200">{t("how_it_works.tournament_dist_3_val")}</strong>{t("how_it_works.tournament_dist_3_suffix")}</p>
              <p>{t("how_it_works.tournament_dist_4")}<strong className="text-amber-200">{t("how_it_works.tournament_dist_4_val")}</strong>{t("how_it_works.tournament_dist_4_suffix")}</p>
            </div>
            <p className="text-white/80">{t("how_it_works.tournament_note")}</p>
          </section>

          <section className="rounded-sm border border-primary/30 bg-black/50 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              <h3 className="font-display font-black text-lg text-primary">{t("how_it_works.fee_title")}</h3>
            </div>
            <ul className="text-white/80 space-y-1.5 list-disc list-inside">
              {feeItems.map((item, i) => (<li key={i}>{item}</li>))}
            </ul>
            <p className="text-xs text-muted-foreground italic pt-1">{t("how_it_works.fee_note")}</p>
          </section>
        </div>
      </div>
    </div>
  );
}
